import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { formatPercent } from '../../../../lib/format.js';
import { BriefField } from '../../BriefCard.js';
import { CategorySection, GEM_GRID_SX } from '../../GemCard.js';
import { IconBadge } from '../../IconBadge.js';
import { CHECKBOX_SX, Pill, PRIMARY_PILL_SX } from '../../Pill.js';
import { CARD_SX, SUCCESS_PILL_SX } from '../primitives.js';
import {
  type ElementSelection,
  elementKeyOf,
  elementKeysOf,
  type IdeationBrief,
} from './briefResult.js';
import { ElementCard } from './ElementCard.js';

const NOTE_SX = (theme: Theme) => ({
  gap: 1.5,
  p: 3,
  borderRadius: 1.2,
  bgcolor: theme.palette.primary.bg,
  alignItems: 'flex-start',
});

// The step offers the suggestions beside what the brief holds; anywhere else the
// brief is only what it holds.
function shownCategories(
  brief: IdeationBrief,
  selection: ElementSelection | undefined,
): IdeationBrief['categories'] {
  if (selection === undefined) return brief.categories;
  const suggested = (name: string) =>
    brief.suggestions.find((category) => category.name === name)?.elements ?? [];
  const names = new Set(brief.categories.map((category) => category.name));
  return [
    ...brief.categories.map((category) => ({
      ...category,
      elements: [...category.elements, ...suggested(category.name)],
    })),
    ...brief.suggestions.filter((category) => !names.has(category.name)),
  ];
}

function SelectAll({
  keys,
  selection,
}: {
  keys: string[];
  selection: ElementSelection | undefined;
}): ReactNode {
  if (selection === undefined || keys.length === 0) return null;
  const chosen = keys.filter((key) => selection.selectedKeys.includes(key)).length;
  return (
    <Stack
      component="label"
      direction="row"
      sx={{ gap: 1.5, alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}
    >
      <Checkbox
        size="small"
        checked={chosen === keys.length}
        indeterminate={chosen > 0 && chosen < keys.length}
        onChange={() => selection.onToggleAll(keys)}
        sx={CHECKBOX_SX}
        slotProps={{ input: { 'aria-label': 'Select all elements' } }}
      />
      <Typography sx={{ fontSize: 12 }}>Select all</Typography>
    </Stack>
  );
}

export function IdeationBriefCard({
  brief,
  selection,
}: {
  brief: IdeationBrief;
  selection?: ElementSelection;
}): ReactNode {
  const categories = shownCategories(brief, selection);
  const elements = categories.flatMap((category) => category.elements);
  const keys = elementKeysOf(brief);
  const names = [...new Set(elements.map((element) => element.tag))];
  const empty = new Set(brief.empty_categories);

  return (
    <Stack data-testid={`ideation-brief-${brief.name}`} sx={{ ...CARD_SX, gap: 4 }}>
      <Stack direction="row" sx={{ gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <IconBadge icon={HubOutlined} size={40} iconSize={20} />
        <Stack sx={{ gap: 0.5, flex: 1, minWidth: 200 }}>
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ fontSize: 16, textWrap: 'balance' }}>
              {brief.name}
            </Typography>
            <Pill sx={SUCCESS_PILL_SX}>BRIEF READY</Pill>
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {elements.length} element{elements.length === 1 ? '' : 's'}
            {names.length === 0 ? '' : ` · ${names.slice(0, 3).join(', ')}`}
            {names.length > 3 ? ` +${names.length - 3} more` : ''}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" sx={NOTE_SX}>
        <InfoOutlined sx={{ fontSize: 14, color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
        <Stack sx={{ gap: 0.5, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
            What to do with this brief
          </Typography>
          <Typography sx={{ fontSize: 12, whiteSpace: 'pre-wrap', textWrap: 'pretty' }}>
            {brief.body}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <BriefField label="KPI to lead on" value={brief.kpi_label || brief.kpi} />
        <BriefField label="Creative type" value={brief.creative_type} />
        <BriefField label="Campaign objective" value={brief.campaign_objectives.join(' · ')} />
        <BriefField label="Concept" value={brief.concepts.join(' · ')} />
        <BriefField label="Format" value={brief.formats.join(' · ')} />
        <BriefField label="Duration" value={brief.durations.join(' · ')} />
      </Box>

      <Stack sx={{ gap: 3 }}>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <AutoAwesomeOutlined sx={{ fontSize: 15, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Assembled elements</Typography>
          <Pill sx={PRIMARY_PILL_SX}>{elements.length} ELEMENTS</Pill>
        </Stack>
        {selection === undefined ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            Pick the elements the storyboard should build on. Unpicked elements are left out.
          </Typography>
        )}
        <SelectAll keys={keys} selection={selection} />
        {categories.map((category) => {
          const best = category.elements
            .flatMap((element) => element.figures.map((figure) => figure.lift))
            .reduce<number | null>((top, lift) => (top === null || lift > top ? lift : top), null);
          const count = category.elements.length;
          return (
            <CategorySection
              key={category.name}
              category={category.name}
              meta={
                `${count} element${count === 1 ? '' : 's'}` +
                (best === null ? '' : ` · Best ↑ ${formatPercent(best - 1, 0)}`)
              }
            >
              {count === 0 ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 1 }}>
                  {empty.has(category.name)
                    ? 'No proven elements found for this category.'
                    : 'No elements in this category.'}
                </Typography>
              ) : (
                <Box sx={GEM_GRID_SX}>
                  {category.elements.map((element) => (
                    <ElementCard
                      key={elementKeyOf(brief.name, category.name, element)}
                      element={element}
                      kpi={brief.kpi}
                      kpiLabel={brief.kpi_label}
                      selectKey={elementKeyOf(brief.name, category.name, element)}
                      selection={selection}
                    />
                  ))}
                </Box>
              )}
            </CategorySection>
          );
        })}
      </Stack>
    </Stack>
  );
}
